import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, Col, ListGroup, FormCheck, Row, Modal, Alert } from 'react-bootstrap';
import GrupperingPicker from 'components/GrupperingPicker';
import { IEnhetsgruppeNode } from 'components/GrupperingPicker/types';
import { IcebergButton, IcebergIcon } from '@tradesolution/iceberg-ui-react';
import EnhetsgrupperingApi from 'services/KjederegisteretAdminApi/EnhetsgrupperingApi';
import { GrupperingForMedlemsgruppe } from 'services/KjederegisteretAdminApi/EnhetsgrupperingApi/types';
import GrupperingSuggestion from './GrupperingSuggestion';
import EnhetApi from 'services/KjederegisteretAdminApi/EnhetApi';
import useIsMountedRef from 'utils/hooks/useIsMountedRef';
import HttpErrorHelper from 'utils/HttpErrorHelper';
import useToaster from 'utils/hooks/useToaster';
import useForm from 'utils/hooks/useForm';
import Style from './index.module.css';
import CommonLoader from 'components/CommonLoader';
import { NorwegianDatepicker } from 'components/NorwegianDatepicker';

interface Props {
  enhetId: number;
  isFetchingGrupperinger: boolean;
  onUpdateEnhet: () => void;
  setIsShowForm: (show: boolean) => void;
  skalBetraktesSomKjedeMedlemskapDefaultChecked: boolean;
  onUpdated: () => void;
  checkForDuplicateGroupings: (Group: string, date: Date) => boolean;
}

export interface GrupperingCommand {
  enhetsgrupperingId: number;
  fraOgMedDato: Date;
  kundenr: string;
  skalBetraktesSomKjedemedlemskap: boolean;
  idOver: number;
  suggestions: GrupperingForMedlemsgruppe[];
  label: string;
  nr: number;
  enhetgrupperingType: string;
}

const AddToGrupperingForm = (props: Props) => {
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showSuggestionHelpText, setShowSuggestionHelpText] = useState<boolean>(false);
  const [duplicateGruppeError, setDuplicateGruppeError] = useState<string | null>(null);
  const fetchedSuggestionIdsRef = React.useRef<Set<number>>(new Set());
  const isMountedRef = useIsMountedRef();
  const toaster = useToaster();
  const initialData: GrupperingCommand[] = [];

  const validate = (values: GrupperingCommand[]) => {
    let errors: any = {};
    return errors;
  };

  const onSubmit = async (values: GrupperingCommand[]) => {
    setIsLoadingSubmit(true);
    const payload = {
      enhetId: props.enhetId,
      grupperinger: values.map((v) => ({
        enhetsgrupperingId: v.enhetsgrupperingId,
        fraOgMedDato: v.fraOgMedDato,
        kundenr: v.kundenr,
        skalBetraktesSomKjedemedlemskap: v.skalBetraktesSomKjedemedlemskap,
      })),
    };

    try {
      await EnhetApi.knyttTilFlereEnhetsgrupperinger(props.enhetId, payload);
      toaster.success('Suksess!', 'Enheten er tilknyttet til enhetsgrupperingen!');
      props.onUpdateEnhet();
      props.onUpdated();
      setIsLoadingSubmit(false);
      resetForm(initialData);
      props.setIsShowForm(false);
    } catch (err) {
      setIsLoadingSubmit(false);
      toaster.error('Feil', HttpErrorHelper.formatError(err));
    }
  };

  const { values, updateValues, handleSubmit, resetForm } = useForm(
    initialData,
    validate,
    onSubmit,
  );

  const onGrupperingSelectionChanged = useCallback(
    (grupperinger: IEnhetsgruppeNode[]) => {
      const indexOfFirstISGruppering = grupperinger.findIndex(
        (o) => o.enhetgrupperingType === 'IS',
      );
      const selected: GrupperingCommand[] = grupperinger?.map(
        (x: IEnhetsgruppeNode, index: number) => ({
          enhetsgrupperingId: x.id,
          fraOgMedDato: null,
          kundenr: null,
          skalBetraktesSomKjedemedlemskap:
            props.skalBetraktesSomKjedeMedlemskapDefaultChecked &&
            index === indexOfFirstISGruppering
              ? true
              : false,
          suggestions: [],
          idOver: x.idOver,
          label: x.label,
          nr: x.nr,
          enhetgrupperingType: x.enhetgrupperingType,
        }),
      );
      updateValues(selected);
      // Clear fetched IDs when selection changes
      fetchedSuggestionIdsRef.current = new Set();
    },
    [updateValues, props.skalBetraktesSomKjedeMedlemskapDefaultChecked],
  );

  const getSuggestions = useCallback(
    async (id: number, currentValues: GrupperingCommand[]) => {
      if (id && !fetchedSuggestionIdsRef.current.has(id)) {
        fetchedSuggestionIdsRef.current.add(id);
        try {
          const res = await EnhetsgrupperingApi.getGrupperingWithMedlemsgruppeById(id);
          const copy = [...currentValues];
          const index = copy.findIndex((x) => x.enhetsgrupperingId === id);
          if (index !== -1) {
            copy[index].suggestions = res.length > 0 ? res : undefined;
            updateValues(copy);
          }
        } catch (err) {
          console.error('Failed to fetch suggestions for', id, err);
        }
      }
    },
    [updateValues],
  );

  const handleSuggestionClicked = (
    selectedId: number,
    label: string,
    idOver: number,
    nr: number,
    typekode: string,
  ) => {
    if (selectedId) {
      if (values.filter((x) => x.enhetsgrupperingId === selectedId).length > 0) {
        const newSelected = values.filter((y) => y.enhetsgrupperingId !== selectedId);
        updateValues(newSelected);
      } else {
        var betraktSomKjedemedlemskap: boolean =
          props.skalBetraktesSomKjedeMedlemskapDefaultChecked &&
          values.filter((x) => x.enhetgrupperingType === 'IS').length === 0;
        if (
          values.filter((z) => z.idOver === idOver || z.enhetsgrupperingId === idOver).length > 0
        ) {
          // hvis allerede valgt med samme idOver.
          const newSelected = values.filter((x) => x.idOver !== idOver);
          newSelected.push({
            enhetsgrupperingId: selectedId,
            label: label,
            suggestions: [],
            fraOgMedDato: null,
            kundenr: null,
            skalBetraktesSomKjedemedlemskap: betraktSomKjedemedlemskap,
            idOver: idOver,
            nr: nr,
            enhetgrupperingType: typekode,
          });
          updateValues(newSelected);
        } else {
          updateValues([
            ...values,
            {
              enhetsgrupperingId: selectedId,
              label: label,
              suggestions: [],
              fraOgMedDato: null,
              kundenr: null,
              skalBetraktesSomKjedemedlemskap: betraktSomKjedemedlemskap,
              idOver: idOver,
              nr: nr,
              enhetgrupperingType: typekode,
            },
          ]);
        }
      }
    }
  };

  const handleGrupperingAsKjedemedlemskapClicked = (id: number) => {
    const copy = [...values];
    copy.map((x: GrupperingCommand) => (x.skalBetraktesSomKjedemedlemskap = false));
    if (id) {
      const index = copy.findIndex((y) => y.enhetsgrupperingId === id);
      copy[index].skalBetraktesSomKjedemedlemskap = true;
    }

    updateValues(copy);
  };

  const handleFraOgMedChange = (id: number, date: Date | undefined) => {
    const copy = [...values];
    const index = copy.findIndex((x) => x.enhetsgrupperingId === id);
    const isValidDate = date instanceof Date && !Number.isNaN(date.getTime());

    if (!isValidDate) {
      copy[index].fraOgMedDato = undefined;
      updateValues(copy);
      setDuplicateGruppeError(null);
      return;
    }

    copy[index].fraOgMedDato = date;
    // sjekk for duplikat IS gruppering og dato
    if (copy[index].enhetgrupperingType === 'IS') {
      const duplicateExits = props.checkForDuplicateGroupings(
        copy[index].enhetgrupperingType,
        date,
      );
      if (duplicateExits) {
        setDuplicateGruppeError(
          'Du kan ikke legge inn 2 innkjøpssamarbeid med samme dato! Bruk en annen dato.',
        );
      } else {
        setDuplicateGruppeError(null);
      }
    }
    updateValues(copy);
  };

  const handleKundenummerChange = (id: number, value: string) => {
    const copy = [...values];
    const index = copy.findIndex((x) => x.enhetsgrupperingId === id);
    copy[index].kundenr = value;
    updateValues(copy);
  };

  useEffect(() => {
    if (isMountedRef.current && values) {
      values.forEach((x) => {
        // Only fetch if suggestions array is empty (length 0) and we haven't fetched this ID yet
        if (
          Array.isArray(x.suggestions) &&
          x.suggestions.length === 0 &&
          !fetchedSuggestionIdsRef.current.has(x.enhetsgrupperingId)
        ) {
          getSuggestions(x.enhetsgrupperingId, values);
        }
      });
    }
  }, [values, isMountedRef, getSuggestions]);

  return (
    <Form onSubmit={handleSubmit}>
      <Row>
        <h4>Knytt til enhetsgrupperinger</h4>
      </Row>

      <Row>
        <Form.Group as={Col} controlId="formEnhetgrupperingForeldre">
          <Form.Label>Enhetsgruppering *</Form.Label>
          <GrupperingPicker
            treeSelectMode="hierarchical"
            onSelectionChanged={onGrupperingSelectionChanged}
            disabled={props.isFetchingGrupperinger}
            closeOnSelect={true}
          />
        </Form.Group>
      </Row>

      {values &&
        values.filter(
          (x) => x.suggestions && x.suggestions.length > 0 && x.suggestions !== undefined,
        ).length > 0 && (
          <Alert key={Math.random() + '-alert'} variant="info">
            Valgt enhetgruppering er medlemsgruppe i andre grupperinger (
            {values.filter((x) => x.suggestions).length}).
            <Alert.Link onClick={() => setShowModal(true)}> Se forslag.</Alert.Link>
          </Alert>
        )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Forslag til enhetsgruppering</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h6>
            <span>Huk av for grupperinger du ønsker å legge til </span>
            <span
              onClick={() => setShowSuggestionHelpText(!showSuggestionHelpText)}
              style={{ cursor: 'pointer' }}
            >
              <IcebergIcon icon="info" />
            </span>
          </h6>
          {showSuggestionHelpText && (
            <p className="text-muted">
              For å gjøre det enklere å huske på å melde en enhet inn i enhetgruppering, får du
              forslag til enhetsgruppering basert på valgt enhetgruppering (OI). Forslag genereres
              basert på hvilken enhetsgruppering valgt enhetgruppering(OI) er medlemsgruppe i.
              Dersom det finnes en undergruppering med samme navn som valgt enhetgruppering(OI),
              vises denne først i listen.
            </p>
          )}
          {values.map((gruppering) => (
            <GrupperingSuggestion
              key={gruppering.enhetsgrupperingId}
              onSetSelectedGrupperinger={handleSuggestionClicked}
              selected={gruppering}
              selectedIDs={values?.map((x) => x.enhetsgrupperingId)}
            />
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowModal(false)}>
            Lukk
          </Button>
        </Modal.Footer>
      </Modal>

      <Row>
        <Form.Group as={Col} controlId="formGrupperingKnr">
          <ListGroup className={Style.selectedGrupperingFormListGroup}>
            {values.map((x) => (
              <ListGroup.Item
                key={x.enhetsgrupperingId}
                className={Style.selectedGrupperingFormListGroupItem}
              >
                <Row>
                  <Col>
                    <Form.Label>{x.label}</Form.Label>
                  </Col>
                </Row>
                <Row>
                  <Col md="4">
                    <Form.Label>
                      Fra og med dato<span style={{ color: 'red' }}>*</span>
                    </Form.Label>
                    <NorwegianDatepicker
                      value={x.fraOgMedDato}
                      placeholder="dd.mm.yyyy"
                      onChange={(date) => handleFraOgMedChange(x.enhetsgrupperingId, date)}
                      showFirstDaySuggestions
                    />

                    {!x.fraOgMedDato && (
                      <span className="text-muted">Mangler fra og med dato!</span>
                    )}
                    {duplicateGruppeError && x.enhetgrupperingType === 'IS' && (
                      <div style={{ color: 'red' }}>{duplicateGruppeError}</div>
                    )}
                  </Col>
                  <Col md="4">
                    <Form.Label>Medlemsnummer</Form.Label>
                    <Form.Control
                      type="text"
                      name="medlemsnummer"
                      placeholder=""
                      value={x.kundenr || ''}
                      onChange={(e) =>
                        handleKundenummerChange(x.enhetsgrupperingId, e.target.value)
                      }
                    />
                    {x && x.kundenr && x.kundenr.toString().match(/^[0-9]+$/) === null && (
                      <span style={{ color: 'red' }}>Kun siffer!</span>
                    )}
                  </Col>
                  {x.enhetgrupperingType === 'IS' && (
                    <Col md="4" className={Style.kjedemedlemskapCol}>
                      <Form.Label>
                        Vis som kjedemedlemskap i fileksport (Gjelder kun 'IS' enhetsgrupperinger)
                      </Form.Label>
                      <FormCheck
                        type="checkbox"
                        className={Style.checkbox}
                        checked={x.skalBetraktesSomKjedemedlemskap}
                        onChange={() =>
                          handleGrupperingAsKjedemedlemskapClicked(
                            x.skalBetraktesSomKjedemedlemskap ? undefined : x.enhetsgrupperingId,
                          )
                        }
                      />
                      <Form.Text muted>
                        Default avhuket hvis enhet ikke har kjede eller gruppemedlemskap
                      </Form.Text>
                    </Col>
                  )}
                </Row>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Form.Group>
      </Row>

      <Row>
        <Col>
          <IcebergButton
            type="submit"
            variant="outline-primary"
            icon="disk"
            className="mt-3"
            isLoading={isLoadingSubmit}
            disabled={
              !!duplicateGruppeError ||
              values?.filter(
                (x) =>
                  !x.fraOgMedDato || (x.kundenr && x.kundenr.toString().match(/^[0-9]+$/) === null),
              ).length > 0 ||
              values.length < 1
            }
          >
            <span>Lagre</span>
          </IcebergButton>
        </Col>
      </Row>
    </Form>
  );
};

export default AddToGrupperingForm;
